import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GenericTable } from "@shared/components/GenericTable";
import { TableAction, TableColumn } from "@shared/types/table";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Plus } from "lucide-react";
import { DeleteConfirmationDialog } from "@shared/components/DeleteConfirmationDialog";
import { ResourceSubtypeForm } from "../create/create";
import { PageHeader } from "@shared/ui/pageHeader";
import { useGetResourceSubtypeList } from "../../../../../hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { PAGE_SIZE } from "@shared/utils/constants";

interface ResourceSubtype {
  id: string;
  name: string;
  code: string;
  resourceType: string;
  fields: string;
  description: string;
  created: string;
  createdBy: string;
}



export function ResourceSubtypeList() {
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const { data: resourceSubtypesResponse, isLoading } = useGetResourceSubtypeList(remoteUtilityId, undefined, currentPage, PAGE_SIZE, searchQuery);

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState<ResourceSubtype | undefined>(undefined);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editData, setEditData] = useState<ResourceSubtype | undefined>(undefined);

  const columns: TableColumn<ResourceSubtype>[] = useMemo(
    () => [
      {
        key: "name",
        header: "Name",
        type: "text",
        render: (value) => <span className="font-medium text-gray-900">{value}</span>,
      },
      {
        key: "configurationCode",
        header: "Code",
        type: "text",
        render: (value) => <span className="font-mono text-gray-700">{value}</span>,
      },
      {
        key: "resourceType",
        header: "Resource Type",
        type: "custom",
        render: (value, row: any) => (
          <Badge variant="secondary" className="bg-blue-50 text-blue-700 hover:bg-blue-50 font-medium">
            {row?.hierarchyNames?.resourceType || value || "NA"}
          </Badge>
        ),
      },
      {
        key: "customFields",
        header: "Fields",
        type: "custom",
        render: (value: any) => {
          if (!Array.isArray(value) || value.length === 0) {
            return <span className="text-gray-500">No fields</span>;
          }
          return (
            <div className="flex flex-wrap gap-1">
              {value.map((field: any, idx: number) => (
                <Badge key={idx} variant="outline" className="text-gray-600 border-gray-200">
                  {field.field_label || field.fieldLabel || field.label || ""}
                </Badge>
              ))}
            </div>
          );
        },
      },

      {
        key: "description",
        header: "Description",
        type: "text",
        render: (value) => <span className="text-gray-500">{value || "No description"}</span>,
      },
      {
        key: "createdDate",
        header: "Created",
        type: "custom",
        render: (value, row) => (
          <div>
            <p className="font-medium text-gray-900">{value}</p>
            <p className="text-[11px] text-gray-500 mt-0.5">{row.createdBy || "System"}</p>
          </div>
        ),
      },
    ],
    []
  );

  const handleView = (row: ResourceSubtype) => {
    navigate(`/mx/settings/resources/sub-type/${row.id}`);
  };

  const handleEdit = (row: ResourceSubtype) => {
    setEditData(row);
    setIsFormOpen(true);
  };

  const handleDelete = (row: ResourceSubtype) => {
    setDeleteItem(row);
    setIsDeleteOpen(true);
  };

  const handleConfirmDelete = () => {
    console.log("Deleted resource sub type:", deleteItem?.id);
    setIsDeleteOpen(false);
    setDeleteItem(undefined);
  };

  const actions: TableAction[] = [
    {
      label: "Edit",
      onClick: handleEdit,
      icon: "Pencil",
      variant: "outline",
    },
    {
      label: "Delete",
      onClick: handleDelete,
      icon: "Trash2",
      variant: "outline",
      disabled: () => true
    },
  ];

  const tableData = resourceSubtypesResponse?.result ?? [];
  const totalItems = resourceSubtypesResponse?.count ?? tableData.length;

  const pagination = useMemo(
    () => ({
      currentPage,
      totalPages: Math.ceil(totalItems / PAGE_SIZE) || 1,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: totalItems === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1,
      endItem: Math.min(currentPage * PAGE_SIZE, totalItems),
    }),
    [currentPage, totalItems]
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <PageHeader
          title="Resource Sub Types"
          description="Manage and configure resource sub types and their attributes"
        />
        <ResourceSubtypeForm
          trigger={
            <Button className="flex items-center gap-1">
              <Plus className="w-4.5 h-4.5" />
              <span>Add Resource Sub Type</span>
            </Button>
          }
        />
      </div>
      <GenericTable
        data={tableData}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        actions={actions}
        actionsType="icons"
        pagination={pagination}
        onPageChange={(page) => setCurrentPage(page)}
        emptyMessage="No resource subtypes found"
        onSearch={(val) => {
          setSearchQuery(val);
          setCurrentPage(1);
        }}
        searchPlaceholder="Search..."
      />

      <ResourceSubtypeForm
        open={isFormOpen}
        onOpenChange={(open) => {
          setIsFormOpen(open);
          if (!open) setEditData(undefined);
        }}
        editData={editData}
      />

      <DeleteConfirmationDialog
        isOpen={isDeleteOpen}
        onClose={() => {
          setIsDeleteOpen(false);
          setDeleteItem(undefined);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete resource sub type"
        itemName={deleteItem?.name}
      />
    </div>
  );
}
